'use client';

import { type IGuestLogin, type ILogin, useUser } from '@/api';
import { useAuth } from '@/api/hooks/useAuth';
import type { IUser } from '@/api/types/user';
import { ACCOUNT_TYPE } from '@/constants';
import { ACCESS_TOKEN, REFRESH_TOKEN, USER } from '@/constants/auth';
import { getOrCreateDeviceInfo } from '@/utils';
import { createContext, useEffect, useState } from 'react';
import { signInWithGoogle } from './firebase';

interface AuthContextType {
	user: IUser | null;
	isGuest: boolean;
	login: (data: ILogin) => void;
	guestLogin: (data: IGuestLogin) => void;
	googleLogin: () => void;
	logout: () => void;
	isLoading: boolean;
	isOwner: (userId: string) => boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(
	undefined,
);

export function AuthProvider({ children }: { children: React.ReactNode }) {
	const [isLoading, setLoading] = useState(true);
	const [user, setUser] = useState<IUser | null>(null);
	const [isGuest, setIsGuest] = useState(true);
	const {
		login: loginMutation,
		logout: logoutMutation,
		guestLogin: guestLoginMutation,
		oauthLogin,
	} = useAuth();
	const { profile, refetchProfile } = useUser();

	const googleLogin = async () => {
		const result = await signInWithGoogle();
		const idToken = await result.user.getIdToken();
		oauthLogin({ idToken });
	};

	useEffect(() => {
		if (profile) {
			setUser(profile);
			setIsGuest(profile.accountType === ACCOUNT_TYPE.GUEST);
			setLoading(false);
			localStorage.setItem(USER, JSON.stringify(profile));
		}
	}, [profile]);

	useEffect(() => {
		const initializeAuth = async () => {
			const accessToken = localStorage.getItem(ACCESS_TOKEN);
			const refreshToken = localStorage.getItem(REFRESH_TOKEN);
			const storedUser = localStorage.getItem(USER);

			if (accessToken || refreshToken) {
				try {
					if (storedUser) {
						const parsedUser = JSON.parse(storedUser);
						setUser(parsedUser);
						setIsGuest(parsedUser.accountType === 'GUEST');
						setLoading(false);
					}
					await refetchProfile();
				} catch (_error) {
					logout();
				}
			} else {
				// If no token, automatically guest login
				await guestLogin({ deviceCode: getOrCreateDeviceInfo().deviceName });
			}
		};

		initializeAuth();
	}, [refetchProfile]);

	const login = async (data: ILogin) => {
		loginMutation(data, {
			onSuccess: async (response) => {
				const { accessToken, refreshToken } = response.data;
				localStorage.setItem(ACCESS_TOKEN, accessToken);
				localStorage.setItem(REFRESH_TOKEN, refreshToken);
				await refetchProfile();
			},
		});
	};

	const guestLogin = async (data: IGuestLogin) => {
		guestLoginMutation(data, {
			onSuccess: async (response) => {
				const { accessToken, refreshToken } = response.data;
				localStorage.setItem(ACCESS_TOKEN, accessToken);
				localStorage.setItem(REFRESH_TOKEN, refreshToken);
				await refetchProfile();
				// router.push('/my_profile/login');
			},
		});
	};

	const logout = () => {
		logoutMutation();
		localStorage.removeItem(ACCESS_TOKEN);
		localStorage.removeItem(REFRESH_TOKEN);
		localStorage.removeItem(USER);
		setUser(null);
		setIsGuest(true);
		setLoading(false);
		guestLogin({ deviceCode: getOrCreateDeviceInfo().deviceName });
	};

	const isOwner = (userId: string) => user?.userId === userId;

	return (
		<AuthContext.Provider
			value={{
				user,
				isGuest,
				login,
				logout,
				guestLogin,
				googleLogin,
				isLoading,
				isOwner,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
}
